import * as React from 'react'
import { useDiagnosticsApi, useInvokeApi, useAuth } from '../../hooks'
import { Formik } from 'formik'
import {
  FormikSelect,
  EditModalActionBar,
  FormikTextField,
} from '../../components'
import {
  Form,
  Box,
  Loader,
  ListItem,
  ListItemText,
  Divider,
  List,
} from 'cs-kevlar-react'
import { Option } from 'cs-kevlar-react/lib/es/Select'
import { Grid } from 'cs-kevlar-react'
import {
  CertificateConfigurationOverrides,
  SupportUserPermission,
} from '../../api'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import * as Yup from 'yup'

interface EditCertificateConfigurationProps {
  onClose?: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'hidden',
      overflowX: 'hidden',
      padding: theme.spacing(8),
    },
    icon: {
      minWidth: '30px',
    },
  }),
)

const booleanOptions: Option[] = [
  {
    key: 'true',
    value: true,
    label: 'true',
  },
  {
    key: 'false',
    value: false,
    label: 'false',
  },
]

export const modelSchema = Yup.object().shape({
  forceCertificateGenerationError: Yup.boolean().required(
    'You must choose one of the values.',
  ),
  deviceCertificateDurationSecondsOverride: Yup.number()
    .nullable()
    .min(0, 'The value must not be negative.'),
  connectorCertificateDurationSecondsOverride: Yup.number()
    .nullable()
    .min(0, 'The value must not be negative.'),
  identityCertificateDurationSecondsOverride: Yup.number()
    .nullable()
    .min(0, 'The value must not be negative.'),
})

export const EditCertificateConfiguration: React.FunctionComponent<EditCertificateConfigurationProps> = ({
  onClose,
}) => {
  const {
    getCertificateConfiguration,
    updateCertificateConfiguration,
  } = useDiagnosticsApi()
  const classes = useStyles()
  const invokeApi = useInvokeApi()
  const { user: authUser } = useAuth()
  const [
    certificateConfiguration,
    setCertificateConfiguration,
  ] = React.useState<CertificateConfigurationOverrides | null>(null)

  React.useEffect(() => {
    invokeApi(
      () => getCertificateConfiguration(),
      'There was an error getting the certificate configuration',
      (result) => {
        setCertificateConfiguration(result)
      },
    )
  }, [])

  if (authUser === null) return null

  const onEdit = (values: CertificateConfigurationOverrides, actions: any) => {
    invokeApi(
      () => updateCertificateConfiguration(values),
      'There was an error updating the certificate configuration',
      (_) => {},
      () => {
        if (onClose !== undefined) {
          onClose()
        }
      },
    )
  }

  const canEdit =
    authUser.isSupport &&
    authUser.permissions.includes(
      SupportUserPermission.ManageDiagnosticConfiguration,
    )

  return (
    <React.Fragment>
      {certificateConfiguration === null && (
        <Loader size="sm" text="Loading Certificate Configuration" />
      )}
      {certificateConfiguration !== null && (
        <Formik
          initialValues={certificateConfiguration}
          validationSchema={modelSchema}
          onSubmit={onEdit}
        >
          {(formikProps) => (
            <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
              <Box className={classes.formFieldContainer}>
                <Grid container direction="column">
                  <Grid item>
                    <FormikSelect
                      formikProps={formikProps}
                      id="forceCertificateGenerationError"
                      fieldName="forceCertificateGenerationError"
                      label="Force Certificate Generation Error"
                      options={booleanOptions}
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikTextField
                      formikProps={formikProps}
                      id="deviceCertificateDurationSecondsOverride"
                      fieldName="deviceCertificateDurationSecondsOverride"
                      label="Device Certificate Duration Seconds Override"
                      type="number"
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikTextField
                      formikProps={formikProps}
                      id="connectorCertificateDurationSecondsOverride"
                      fieldName="connectorCertificateDurationSecondsOverride"
                      label="Connector Certificate Duration Seconds Override"
                      type="number"
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikTextField
                      formikProps={formikProps}
                      id="identityCertificateDurationSecondsOverride"
                      fieldName="identityCertificateDurationSecondsOverride"
                      label="Identity Certificate Duration Seconds Override"
                      type="number"
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Box className={classes.spacer} />
                </Grid>
              </Box>
              <List dense={true}>
                <ListItem>
                  <ListItemText
                    primary="Note"
                    secondary="The empty values won't override anything and will default to using what's defined in the normal app configuration."
                  />
                </ListItem>
              </List>
              {
                <EditModalActionBar
                  canDelete={false}
                  onCancel={onClose}
                  canSubmit={canEdit}
                  formikProps={formikProps}
                />
              }
            </Form>
          )}
        </Formik>
      )}
    </React.Fragment>
  )
}
